/* Responsibility: Creates a travel slider */
/**/
import preconditions from './preconditions.js';
const checkPreconditions = preconditions('createSlider', {
    root: 'element',
    from: 'input',
    to: 'input',
});

export default function createSlider(params) {
    checkPreconditions(params);

    if (module.hot) {
        // REIS-2732 Due to slider adding dom elements, we need to remove them on hot deloying new modules with webpack HMR.
        params.root.innerHTML = '';
        module.hot.accept();
    }

    params.root.appendChild(params.from);
    params.root.appendChild(params.to);

    if (typeof params.minValue !== 'undefined' && typeof params.maxValue !== 'undefined') {
        params.from.setAttribute('max', params.maxValue);
        params.from.setAttribute('min', params.minValue);
        params.from.setAttribute('value', params.minValue);
        params.from.setAttribute('data-default', params.minValue);
        params.to.setAttribute('max', params.maxValue);
        params.to.setAttribute('min', params.minValue);
        params.to.setAttribute('value', params.maxValue);
        params.to.setAttribute('data-default', params.maxValue);
    }
    return new Slider(params.from, params.to, params.opts);
}

/**
 * Creates a slider for picking intervals or numbers.
 *
 * A slider can be created with one or two draggable thumbs indicating the values.
 *
 * A slider with one thumb is created with
 *
 *   new Slider(startInputElement)
 *
 * A slider with two thumbs is created with
 *
 *   new Slider(startInputElement, endInputElement)
 *
 * The startInputElement must have a `min` attribute specified. If the slider has one thumb,
 * the startInputElement must also have a `max` attribute specified. Otherwise, the `max`
 * attribute must be specified on the endInputElement.
 *
 * The `step` attribute may be specified on the startInputElement, in which case values
 * will be set in steps.
 *
 * The `value` attribute may be specified on both elements, defining the initial value. If it's
 * not specified, the initial values will be set to the minimum and maximum values.
 *
 * The slider's interval will be the closed interval
 *
 *   [startInputElement.min, endInputElement.max]
 *
 * If the `data-unbounded-end` attribute is specified on one of the elements, the corresponding
 * end will be unbounded which means that the value of the element, upon reaching its maximum or
 * minimum value, will be set to the empty string.
 *
 * Requires Function.prototype.bind.
 */

/*

 TODO:
 Clean up initialization
 Handle division by zero
 Implement ARIA
 */

function Slider(startInputEle, endInputEle, options) {
    if (!startInputEle || !endInputEle) {
        throw new Error(`Input elements has to be specified.`);
    }

    this._handleMouseDown = this._handleMouseDown.bind(this);
    this._handleMouseUp = this._handleMouseUp.bind(this);
    this._handleMouseMove = this._handleMouseMove.bind(this);
    this._handleTouchStart = this._handleTouchStart.bind(this);
    this._handleTouchEnd = this._handleTouchEnd.bind(this);
    this._handleTouchMove = this._handleTouchMove.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._calculateSize = this._calculateSize.bind(this);

    options = options || {};

    this._sliderEle = options.container;
    this._fixedSize = options.fixedSize;

    this._setupSlider(startInputEle, endInputEle, options);

    if (options.startFormatter && options.endFormatter) {
        this._startFormat = options.startFormatter;
        this._endFormat = options.endFormatter;
    } else {
        this._startFormat = options.formatter;
        this._endFormat = options.formatter;
    }

    // Not using width because of IE8
    if (this._fixedSize) {
        this._thumbWidth = options.fixedSize.thumbWidth;
        this._sliderEle.style.width = `${options.fixedSize.width + this._thumbWidth}px`;
        this._trackEle.style.width = `${options.fixedSize.width}px`;
    } else {
        this._thumbWidth = this._getThumbWidth();
    }
    this._sliderEleLeft = this._sliderEle.getBoundingClientRect().left;
    this._setOutputValue(this._startValue, this._endValue);

    this._activeThumb = null;
    this._dragStartX = 0;
    this._scaled = 0;
    this._sliderWidth = 0;

    this._HALF_THUMB_WIDTH = Number((this._thumbWidth / 2).toFixed(1));

    // handleResize sets the scale and positions the thumbs, so
    // run it immediately
    this._calculateSize();
    if (!this._fixedSize) {
        this._addEventListener(window, 'resize', this._calculateSize);
    }
}

Slider.prototype = {
    /**
     * Sets the start value of the slider.
     */
    setStartValue(value) {
        if (typeof value != 'number') {
            throw new Error(`Value must be a number.`);
        }
        this._setStartValue(value);
        this._setStartThumbPosition(this._getScaledValue(this._startValue));
    },

    /**
     * Sets the end value of the slider.
     */
    setEndValue(value) {
        if (typeof value != 'number') {
            throw new Error(`Value must be a number.`);
        }
        this._setEndValue(value);
        this._setEndThumbPosition(this._getScaledValue(this._endValue));
    },

    /**
     * Sets the min value of the slider.
     */
    setMinValue(value) {
        if (typeof value != 'number') {
            throw new Error(`Value must be a number.`);
        }
        const userHasChangedSlider = this._startValue > this._minValue;
        this._minValue = value;
        this._startInputEle.setAttribute('min', value);
        this._startThumbEle.setAttribute('aria-valuemin', this._minValue);
        if (this._endInputEle) {
            this._endInputEle.setAttribute('min', value);
            this._endThumbEle.setAttribute('aria-valuemin', this._minValue);
        }
        this._calculateSize();
        if (!userHasChangedSlider) {
            this.setStartValue(value);
        }
    },

    /**
     * Sets the max value of the slider.
     */
    setMaxValue(value) {
        if (typeof value != 'number') {
            throw new Error(`Value must be a number.`);
        }
        const userHasChangedSlider = this._endValue < this._maxValue;
        this._maxValue = value;
        this._startInputEle.setAttribute('max', value);
        this._startThumbEle.setAttribute('aria-valuemax', this._maxValue);
        if (this._endInputEle) {
            this._endInputEle.setAttribute('max', value);
            this._endThumbEle.setAttribute('aria-valuemax', this._maxValue);
        }
        this._calculateSize();
        if (!userHasChangedSlider) {
            this.setEndValue(value);
        }
    },

    /**
     * Sets the size if the slider. In cases where the slider element has
     * display set to none while the slider initializes, this has to be run
     * after the display is set to something else.
     *
     * TODO: maybe a better name
     */
    layout() {
        // TODO: clean this up
        this._thumbWidth = this._getThumbWidth();
        this._sliderEleLeft = this._sliderEle.getBoundingClientRect().left;
        this._HALF_THUMB_WIDTH = Number((this._thumbWidth / 2).toFixed(1));
        this._endThumbLeft = this._sliderEle.offsetWidth - this._thumbWidth;
        this._calculateSize();
    },

    // Simplistic implementation
    dispatchEvent(event) {
        this._sliderEle.dispatchEvent(event);
    },

    // Simplistic implementation
    addEventListener(type, callback, capture) {
        this._addEventListener(this._sliderEle, type, callback, capture || false);
    },

    // Simplistic implementation
    removeEventListener(type, callback, capture) {
        this._removeEventListener(this._sliderEle, type, callback, capture || false);
    },

    _getThumbWidth() {
        const _element = this._startThumbEle.getBoundingClientRect().width > 0 ? this._startThumbEle : this._endThumbEle;
        return _element.getBoundingClientRect().right - _element.getBoundingClientRect().left;
    },

    _setupSlider(startInputEle, endInputEle, options) {
        this._isSingleSliderMin = options.single && options.single.min;
        this._isSingleSliderMax = options.single && options.single.max;
        this._startInputEle = startInputEle;
        this._endInputEle = endInputEle;

        // Using getAttribute not directly on property due to IE
        this._minValue = Number(startInputEle.getAttribute('min'));
        this._maxValue = Number(endInputEle.getAttribute('max'));
        this._step = Number(startInputEle.getAttribute('step')) || 1;
        endInputEle.step = this._step;
        this._startValue = startInputEle.value === '' ? this._minValue : Number(startInputEle.value);
        this._endValue = endInputEle.value === '' ? this._maxValue : Number(endInputEle.value);
        const leftUnboundedAttr = startInputEle.getAttribute('data-unbounded-end');
        const rightUnboundedAttr = endInputEle.getAttribute('data-unbounded-end');
        this._isLeftUnboundedInterval = leftUnboundedAttr != null && leftUnboundedAttr !== 'false';
        this._isRightUnboundedInterval = rightUnboundedAttr != null && rightUnboundedAttr !== 'false';
        this._startInputPrefix = startInputEle.getAttribute('data-output-prefix') || '';
        this._startInputSuffix = startInputEle.getAttribute('data-output-suffix') || '';
        this._endInputPrefix = endInputEle.getAttribute('data-output-prefix') || '';
        this._endInputSuffix = endInputEle.getAttribute('data-output-suffix') || '';
        this._outputLabel = startInputEle.getAttribute('data-unbounded=output') || '{value}';
        this._startUnboundedOutputLabel = startInputEle.getAttribute('data-unbounded-output') || '{value}';
        this._endUnboundedOutputLabel = endInputEle.getAttribute('data-unbounded-output') || '{value}';

        if (this._sliderEle) {
            const thumbs = this._sliderEle.querySelectorAll('.finn-slider-thumb');
            this._trackInnerEle = this._sliderEle.querySelector('.finn-slider-track-inner');
            this._outputEle = this._sliderEle.querySelector('.finn-slider-output');
            this._startThumbEle = thumbs[0];
            this._endThumbEle = thumbs[1];
            this._endThumbEle.style.removeProperty('right');
        } else {
            this._createElements();
        }

        if (!this._startThumbEle.hasAttribute('tabindex')) {
            this._startThumbEle.tabIndex = 0;
        }

        if (!this._endThumbEle.hasAttribute('tabindex')) {
            this._endThumbEle.tabIndex = 0;
        }

        // ARIA
        // TODO: aria-labelledby, aria-label should be configurable
        // update valuemin and valuemax dynamivally?
        this._startThumbEle.setAttribute('role', 'slider');
        this._startThumbEle.setAttribute('aria-valuemin', this._minValue);
        this._startThumbEle.setAttribute('aria-valuemax', this._maxValue);
        this._startThumbEle.setAttribute('aria-valuenow', this._startValue);
        this._startThumbEle.setAttribute('aria-valuetext', this._startValue);
        this._startThumbEle.setAttribute('aria-label', 'Start');
        this._endThumbEle.setAttribute('role', 'slider');
        this._endThumbEle.setAttribute('aria-valuemin', this._minValue);
        this._endThumbEle.setAttribute('aria-valuemax', this._maxValue);
        this._endThumbEle.setAttribute('aria-valuenow', this._endValue);
        this._endThumbEle.setAttribute('aria-valuetext', this._endValue);
        this._endThumbEle.setAttribute('aria-label', 'End');

        this._setStartInputValue(this._startValue);
        this._setEndInputValue(this._endValue);

        if (this._isSingleSliderMax) {
            this._startThumbEle.classList.add('hidden');
        } else if (this._isSingleSliderMin) {
            this._startThumbEle.classList.add('hidden');
        }

        // Using blur, since IE8 does not support custom events,
        // and we do not want the Slider to fire change events when slider is moved around.
        this._addEventListener(this._startInputEle, 'blur', (event) => {
            if (!event.isSynthetic) {
                this.setStartValue(Number(event.target ? event.target.value : event.srcElement.value));
            }
        });

        this._addEventListener(this._endInputEle, 'blur', (event) => {
            if (!event.isSynthetic) {
                this.setEndValue(Number(event.target ? event.target.value : event.srcElement.value));
            }
        });

        this._addEventListener(this._startThumbEle, 'mousedown', this._handleMouseDown);
        this._addEventListener(this._endThumbEle, 'mousedown', this._handleMouseDown);
        this._addEventListener(this._startThumbEle, 'touchstart', this._handleTouchStart);
        this._addEventListener(this._endThumbEle, 'touchstart', this._handleTouchStart);
        this._addEventListener(this._startThumbEle, 'keydown', this._handleKeyDown);
        this._addEventListener(this._endThumbEle, 'keydown', this._handleKeyDown);
    },

    _createElements() {
        this._sliderEle = document.createElement('div');
        this._sliderEle.className = 'finn-slider';
        this._trackEle = document.createElement('div');
        this._trackEle.className = 'finn-slider-track';
        this._trackInnerEle = document.createElement('div');
        this._trackInnerEle.className = 'finn-slider-track-inner';
        this._startThumbEle = document.createElement('div');
        this._startThumbEle.setAttribute('data-testid', 'startThumb');
        this._startThumbEle.className = 'finn-slider-thumb';
        this._outputEle = document.createElement('div');
        this._outputEle.className = 'finn-slider-output';
        this._sliderEle.appendChild(this._trackEle);
        this._sliderEle.appendChild(this._trackInnerEle);
        this._sliderEle.appendChild(this._startThumbEle);
        this._endThumbEle = document.createElement('div');
        this._endThumbEle.className = 'finn-slider-thumb';
        this._endThumbEle.setAttribute('data-testid', 'endThumb');
        this._sliderEle.appendChild(this._endThumbEle);
        this._sliderEle.insertBefore(this._outputEle, this._sliderEle.firstChild);
        this._startInputEle.parentNode.insertBefore(this._sliderEle, this._startInputEle);
    },

    _setStartValue(value) {
        const newValue = Math.round(this._clamp(value, this._minValue, this._endValue));
        if (this._startValue !== value) {
            this._startValue = newValue;
            this._setOutputValue(newValue, this._endValue);
            this._setStartInputValue(newValue);
            this._startThumbEle.setAttribute('aria-valuenow', newValue);
            this._startThumbEle.setAttribute('aria-valuetext', newValue);
        }
    },

    _setEndValue(value) {
        const newValue = Math.round(this._clamp(value, this._startValue, this._maxValue));
        if (this._endValue !== value) {
            this._endValue = newValue;
            this._setOutputValue(this._startValue, newValue);
            this._setEndInputValue(newValue);
            this._endThumbEle.setAttribute('aria-valuenow', newValue);
            this._endThumbEle.setAttribute('aria-valuetext', newValue);
        }
    },

    _setStartInputValue(value) {
        if (value === this._minValue && this._isLeftUnboundedInterval) {
            value = '';
        }
        this._startInputEle.value = value;
        this._fireBlurEvent(this._startInputEle);
    },

    _setEndInputValue(value) {
        if (value === this._maxValue && this._isRightUnboundedInterval) {
            value = '';
        }
        this._endInputEle.value = value;
        this._fireBlurEvent(this._endInputEle);
    },

    // TODO: this will be changed
    _setOutputValue(start, end) {
        let formattedStartValue = String(start);
        let formattedEndValue = String(end);
        let startLabel = this._outputLabel;
        let endLabel = this._outputLabel;

        if (this._startFormat && !this._isSingleSliderMax) {
            formattedStartValue = this._startFormat(start);
        }

        if (this._endFormat && !this._isSingleSliderMin) {
            formattedEndValue = this._endFormat(end, start);
        }

        if (start === this._minValue && this._startUnboundedOutputLabel) {
            startLabel = this._startUnboundedOutputLabel;
        }

        if (end === this._maxValue && this._endUnboundedOutputLabel) {
            endLabel = this._endUnboundedOutputLabel;
        }

        let value;
        const diff = start - end;
        if (this._isSingleSliderMin || this._isSingleSliderMax) {
            if (this._isSingleSliderMin) {
                value = `${this._startInputPrefix} ${startLabel.replace('{value}', formattedStartValue)} ${this._startInputSuffix}`;
            } else {
                value = `${this._endInputPrefix} ${endLabel.replace('{value}', formattedEndValue)} ${this._endInputSuffix}`;
            }
        } else if (diff > 0 || diff < 0) {
            value = `${this._startInputPrefix} ${startLabel.replace('{value}', formattedStartValue)} ${this._startInputSuffix} – ${
                this._endInputPrefix
            } ${endLabel.replace('{value}', formattedEndValue)} ${this._endInputSuffix}`;
        } else {
            value = `${this._startInputPrefix} ${(start === this._minValue ? startLabel : endLabel).replace(
                '{value}',
                formattedStartValue
            )} ${this._endInputSuffix}`;
        }
        this._outputEle.innerHTML = value;
    },

    _setStartThumbPosition(value) {
        this._startThumbLeft = value;
        this._startThumbEle.style.left = `${value}px`;
        this._trackInnerEle.style.left = `${value + this._HALF_THUMB_WIDTH}px`;
    },

    _setEndThumbPosition(value) {
        this._endThumbLeft = value;
        if (value + this._thumbWidth < this._sliderWidth) {
            this._endThumbEle.style.left = `${value}px`;
            this._endThumbEle.style.removeProperty('right');
        } else {
            this._endThumbEle.style.right = `${0}px`;
            this._endThumbEle.style.removeProperty('left');
        }

        this._trackInnerEle.style.right = `${this._sliderWidth - value - this._HALF_THUMB_WIDTH}px`;
    },

    _calculateScaled(sliderWidth) {
        return sliderWidth / (this._maxValue - this._minValue);
    },

    _getScaledValue(value) {
        return (value - this._minValue) * this._scaled;
    },

    _handleThumbDrag(target, pageX) {
        // TODO: clean this up
        const thumbPos = pageX - this._sliderEleLeft - this._dragStartX;
        const value = this._minValue + thumbPos / this._scaled;
        const stepValue = Math.round(value / this._step) * this._step;
        if (target === this._startThumbEle) {
            this._setStartValue(stepValue);
            this._setStartThumbPosition(this._clamp(thumbPos, 0, this._endThumbLeft));
        } else if (target === this._endThumbEle) {
            this._setEndValue(stepValue);
            this._setEndThumbPosition(this._clamp(thumbPos, this._startThumbLeft, this._sliderWidth - this._thumbWidth));
        }
    },

    _clamp(actual, min, max) {
        return Math.min(Math.max(actual, min), max);
    },

    _fireChangeEvent(ele) {
        this._createEvent(ele, 'change', true);
    },

    _fireBlurEvent(ele) {
        this._createEvent(ele, 'blur', true);
    },

    // Support for IE8: does not polyfill this due to creatEvent hard to polyfill.
    _addEventListener(element, type, callback, capture) {
        if (element.addEventListener) {
            element.addEventListener(type, callback, capture);
        } else {
            element.attachEvent(`on${type}`, callback);
        }
    },

    // Support for IE8: does not polyfill this due to creatEvent hard to polyfill.
    _removeEventListener(element, type, handler, capture) {
        if (element.removeEventListener) {
            element.removeEventListener(type, handler, capture);
        } else {
            element.detachEvent(`on${type}`, handler);
        }
    },

    _createEvent(element, eventType, isSyntetic) {
        if (document.createEvent) {
            const evObj = document.createEvent('Events');
            evObj.initEvent(eventType, true, true);
            evObj.isSynthetic = isSyntetic;
            element.dispatchEvent(evObj);
        } else if (document.createEventObject) {
            const e = document.createEventObject();
            e.eventType = eventType;
            element.fireEvent(`on${e.eventType}`, e);
        }
    },

    _handleMouseDown(event) {
        this._stopPropagation(event);
        this._preventDefault(event);

        const button = event.which ? event.which : event.button;
        if (button !== 1) {
            return;
        }

        const target = event.target ? event.target : event.srcElement;
        const pageX = event.pageX ? event.pageX : event.clientX;
        // Could be event.offsetX, but not all browsers support
        this._dragStartX = pageX - target.offsetLeft - this._sliderEleLeft;

        // TODO: within some threshold?
        if (this._startThumbLeft === this._endThumbLeft) {
            this._observeMouseMoveDirection(target, pageX);
        }

        this._addMouseListeners(target);
    },

    _handleMouseUp(event) {
        this._stopPropagation(event);

        // TODO: only update if some value has changed
        if (this._activeThumb === this._startThumbEle) {
            this._fireChangeEvent(this._startInputEle);
        } else if (this._activeThumb === this._endThumbEle) {
            this._fireChangeEvent(this._endInputEle);
        }
        this._removeTemporaryStyles(this._activeThumb);
        this._activeThumb = null;
        this._dragStartX = 0;
        this._removeEventListener(document, 'mouseup', this._handleMouseUp);
        this._removeEventListener(document, 'mousemove', this._handleMouseMove);
    },

    _handleMouseMove(event) {
        this._stopPropagation(event);
        this._preventDefault(event);

        this._handleThumbDrag(this._activeThumb, event.pageX ? event.pageX : event.clientX);
    },

    _handleTouchStart(event) {
        // The following currently breaks in Chrome on Android, possibly related
        // to https://code.google.com/p/chromium/issues/detail?id=248522
        // event.stopPropagation();
        // event.preventDefault();

        // Prevent more than one touch on the same slider
        if (this._activeThumb) {
            return;
        }

        const target = event.target;
        this._dragStartX = event.targetTouches[0].pageX - target.offsetLeft - this._sliderEleLeft;

        // TODO: within some threshold?
        if (this._startThumbLeft === this._endThumbLeft) {
            this._observeTouchMoveDirection(target, event.targetTouches[0].pageX);
        }

        // We want to skip the next mousedown handler at this point, we'll
        // add it back later.
        // Just set a skipNextMouseDown flag instead?
        target.removeEventListener('mousedown', this._handleMouseDown);

        this._addTouchListeners(target);
    },

    _handleTouchEnd(event) {
        this._stopPropagation(event);

        // TODO: only update if some value has changed
        if (this._activeThumb === this._startThumbEle) {
            this._fireChangeEvent(this._startInputEle);
        } else if (this._activeThumb === this._endThumbEle) {
            this._fireChangeEvent(this._endInputEle);
        }
        const target = event.target;
        this._removeTemporaryStyles(this._activeThumb);
        this._activeThumb = null;
        this._dragStartX = 0;

        this._addEventListener(target, 'mousedown', this._handleMouseDown);
        this._removeEventListener(target, 'touchend', this._handleTouchEnd);
        this._removeEventListener(target, 'touchcancel', this._handleTouchEnd);
        this._removeEventListener(target, 'touchmove', this._handleTouchMove);
    },

    _handleTouchMove(event) {
        this._stopPropagation(event);
        this._preventDefault(event);

        const touches = event.targetTouches;
        const touch = touches[0];
        if (!touch) {
            return;
        }
        this._handleThumbDrag(this._activeThumb, touch.pageX);
    },

    _handleKeyDown(event) {
        this._stopPropagation(event);

        const isStartThumb = event.target === this._startThumbEle;

        let value = isStartThumb ? this._startValue : this._endValue;

        switch (event.key || event.keyIdentifier) {
            case 'Left':
            case 'ArrowLeft':
            case 'Down':
            case 'ArrowDown':
                value -= this._step;
                break;
            case 'Right':
            case 'ArrowRight':
            case 'Up':
            case 'ArrowUp':
                value += this._step;
                break;
            case 'PageDown':
                value = this._minValue;
                break;
            case 'PageUp':
                value = this._maxValue;
                break;
            default:
                return;
        }

        if (isStartThumb) {
            this.setStartValue(value);
            this._fireChangeEvent(this._startInputEle);
        } else {
            this.setEndValue(value);
            this._fireChangeEvent(this._endInputEle);
        }
        this._preventDefault(event);
    },

    _addMouseListeners(target) {
        this._activeThumb = target;
        this._addTemporaryStyles(target);
        this._addEventListener(document, 'mouseup', this._handleMouseUp);
        this._addEventListener(document, 'mousemove', this._handleMouseMove);
    },

    _addTouchListeners(target) {
        this._activeThumb = target;
        this._addTemporaryStyles(target);

        this._addEventListener(target, 'touchend', this._handleTouchEnd);
        this._addEventListener(target, 'touchcancel', this._handleTouchEnd);
        this._addEventListener(target, 'touchmove', this._handleTouchMove);
    },

    _observeMouseMoveDirection(target, start) {
        const handleMouseMove = function (event) {
            const pageX = event.pageX ? event.pageX : event.clientX;
            const actualTarget = pageX < start ? this._startThumbEle : this._endThumbEle;
            this._removeEventListener(target, 'mousemove', handleMouseMove);
            this._removeTemporaryStyles(target);
            this._activeThumb = actualTarget;
            this._addMouseListeners(actualTarget);
        }.bind(this);
        this._addEventListener(target, 'mousemove', handleMouseMove);
    },

    _observeTouchMoveDirection(target, start) {
        const handleTouchMove = function (event) {
            const actualTarget = event.targetTouches[0].pageX < start ? this._startThumbEle : this._endThumbEle;
            target.removeEventListener('touchmove', handleTouchMove);
            this._removeTemporaryStyles(target);
            this._activeThumb = actualTarget;
            this._addTouchListeners(actualTarget);
        }.bind(this);
        target.addEventListener('touchmove', handleTouchMove);
    },

    _calculateSize() {
        if (this._fixedSize) {
            this._sliderWidth = this._fixedSize.width + this._fixedSize.thumbWidth;
        } else {
            this._sliderWidth = this._sliderEle.offsetWidth;
        }
        this._scaled = this._calculateScaled(this._sliderWidth - this._thumbWidth);
        this._setStartThumbPosition(this._getScaledValue(this._startValue));
        this._setEndThumbPosition(this._getScaledValue(this._endValue));
    },

    _addTemporaryStyles(target) {
        const style = target.style;
        style.setProperty('z-index', '1');
        style.setProperty('-webkit-transform', 'translate3d(0, 0, 0)');
        // TODO: This causes painting artifacts in Firefox Mobile 21 beta
        style.setProperty('transform', 'translate3d(0, 0, 0)');
    },

    _removeTemporaryStyles(target) {
        const style = target.style;
        style.removeProperty('-webkit-transform');
        style.removeProperty('transform');
        style.removeProperty('z-index');
    },

    _stopPropagation(event) {
        return event.stopPropagation ? event.stopPropagation() : (event.returnValue = false);
    },

    _preventDefault(event) {
        return event.preventDefault ? event.preventDefault() : (event.returnValue = false);
    },
};
